import React, { useRef, useState } from 'react';
import useSWR from 'swr';
import { FileArchive, Upload, Download, Trash2, Loader2, Lock, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatDate } from '@/lib/format';
import { sha256File } from '@/lib/estimating/parser';
import { listReferenceDocs, uploadReferenceDoc, removeReferenceDoc, signedUrl } from '@/lib/estimating/service';
import { EST_IDS } from '@/constants/testIds/estimating';

export default function ReferenceDocs({ projectId, confirmedTenderId }) {
  const { user, organisationId, role } = useAuth();
  const canView = ['owner', 'admin', 'manager'].includes(role || '');
  const canEdit = ['owner', 'admin'].includes(role || '');
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const { data: docs, mutate, isLoading } = useSWR(canView && projectId ? ['ref-docs', projectId] : null, () => listReferenceDocs(projectId));

  const handleUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const sha256 = await sha256File(file);
      await uploadReferenceDoc({ file, organisationId, projectId, userId: user?.id, sha256, linkedImportId: confirmedTenderId || null });
      toast.success('Original workbook stored');
      mutate();
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDownload = async (doc) => {
    try { window.open(await signedUrl(doc.storage_bucket, doc.storage_path), '_blank', 'noopener'); }
    catch (e) { toast.error(e.message || 'Could not create download link'); }
  };

  const handleRemove = async (doc) => {
    if (!window.confirm(`Remove "${doc.file_name}"? Files linked to a confirmed tender are archived, not deleted.`)) return;
    try {
      const result = await removeReferenceDoc(doc);
      toast.success(result === 'archived' ? 'Workbook archived (linked to a confirmed tender)' : 'Workbook deleted');
      mutate();
    } catch (e) { toast.error(e.message || 'Could not remove'); }
  };

  return (
    <section data-testid={EST_IDS.refDocsSection} className="rounded-lg border border-border bg-white">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
        <div>
          <div className="font-display font-semibold text-lg text-charcoal flex items-center gap-2">Original QS workbooks <Lock size={14} className="text-muted-foreground" /></div>
          <div className="text-xs text-muted-foreground mt-0.5">Optional reference documents. Not used by the importer. Owner/admin manage · managers can download · staff have no access.</div>
        </div>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.xlsm,.pdf" className="hidden" data-testid={EST_IDS.refDocUpload} onChange={(e) => handleUpload(e.target.files?.[0])} />
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />} Upload workbook
            </Button>
          </>
        )}
      </div>
      {!canView ? (
        <div className="px-5 py-6 text-sm text-muted-foreground">Restricted.</div>
      ) : isLoading ? (
        <div className="px-5 py-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : !docs?.length ? (
        <div className="px-5 py-8 text-center text-sm text-muted-foreground"><FileArchive size={20} className="mx-auto mb-2 text-bronze" /> No reference workbooks uploaded.</div>
      ) : (
        <ul className="divide-y divide-border">
          {docs.map((d) => (
            <li key={d.id} data-testid={EST_IDS.refDocRow} className="px-5 py-3 flex items-center gap-3 text-sm">
              <FileArchive size={16} className="text-bronze flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-charcoal">{d.file_name}</div>
                <div className="text-xs text-muted-foreground">{formatDate(d.uploaded_at)} · {d.file_size_bytes ? `${(d.file_size_bytes / 1024).toFixed(0)} KB` : ''}{d.file_sha256 ? ` · sha256 ${d.file_sha256.slice(0, 12)}…` : ''}</div>
              </div>
              {d.archived && <Badge className="bg-muted text-muted-foreground border-0"><Archive size={12} className="mr-1" /> Archived</Badge>}
              <Button type="button" size="sm" variant="ghost" data-testid={EST_IDS.refDocDownload} onClick={() => handleDownload(d)}><Download size={14} /></Button>
              {canEdit && !d.archived && (
                <Button type="button" size="sm" variant="ghost" data-testid={EST_IDS.refDocRemove} onClick={() => handleRemove(d)} className="text-red-700"><Trash2 size={14} /></Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
